import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { createWorker } from "tesseract.js";
import TextField from "./TextField";
import "./NotesHandler.css";
import "./ocr-styles.css";
import Button from "./Button";
import Dropdown from "./Dropdown";
import FolderSidebar from "./FolderSidebar";
import PageHeader from "./PageHeader";
import TopBar from "./TopBar";
import { sounds } from "../delight/sounds";
import {
  NOTES_KEY,
  FOLDERS_KEY,
  PINNED_KEY,
  newId,
  loadNotes,
  saveNotes,
  loadFolders,
  saveFolders,
  loadPinnedIds,
  savePinnedIds,
} from "../utils/storage";

const SORT_OPTIONS = [
  { id: "updated-desc", label: "Last updated (newest)" },
  { id: "updated-asc", label: "Last updated (oldest)" },
  { id: "created-desc", label: "Created (newest)" },
  { id: "created-asc", label: "Created (oldest)" },
  { id: "title-asc", label: "Title (A-Z)" },
  { id: "title-desc", label: "Title (Z-A)" },
];

const NOTE_TYPE_OPTIONS = [
  { id: "all", label: "All types" },
  { id: "text", label: "Notes" },
  { id: "code", label: "Code" },
];

const TRASH_VIEW_ID = "__recently_deleted__";
const AUTO_DELETE_AFTER_MS = 10 * 24 * 60 * 60 * 1000;

// Stable NoteItem component defined outside the parent to avoid remounts on each render.
// Defining a memoized component inside a parent recreates its type every render,
// causing React to unmount/remount children (losing focus in editors).
const NoteItem = React.memo(function NoteItem({
  note,
  updateNote,
  commitNoteEdits,
  handleRemove,
  isPinned,
  onTogglePin,
  folders,
  onMoveNote,
  isEditing,
  setEditingNoteId,
}) {
  // One stable set of handlers per note, so TextField doesn't re-render for nothing
  const handlers = useMemo(() => {
    const id = note.id;
    const set = (patch) => updateNote(id, patch);
    return {
      onChange: (content) => updateNote(id, { content }, { commit: false }),
      onTitleChange: (title) => updateNote(id, { title }, { commit: false }),
      onCommitEdit: () => commitNoteEdits(id),
      onFontChange: (font) => set({ font }),
      onFontSizeChange: (size) => set({ fontSize: Number(size) }),
      onThemeChange: (theme) => set({ theme }),
      onLanguageChange: (language) => set({ language }),
      onCodeColorThemeChange: (codeColorTheme) => set({ codeColorTheme }),
      onShowLineNumbersChange: (showLineNumbers) => set({ showLineNumbers }),
      onRemove: () => handleRemove(id),
      onTogglePin: () => onTogglePin(id),
      onMoveNote: (folderId) => onMoveNote(id, folderId),
      onRequestEdit: () => setEditingNoteId(id),
      onExitEdit: () => setEditingNoteId(null),
    };
  }, [
    note.id,
    updateNote,
    commitNoteEdits,
    handleRemove,
    onTogglePin,
    onMoveNote,
    setEditingNoteId,
  ]);

  return (
    <div className={`note-item${isEditing ? " note-item--editing" : ""}`}>
      <TextField
        value={note.content}
        title={note.title}
        font={note.font}
        fontSize={note.fontSize}
        theme={note.theme}
        noteType={note.noteType || "text"}
        language={note.language || "javascript"}
        codeColorTheme={note.codeColorTheme || "night-owl"}
        showLineNumbers={Boolean(note.showLineNumbers)}
        lastModified={note.lastModified}
        noteId={note.id}
        {...handlers}
        isPinned={isPinned}
        isEditing={isEditing}
        folders={folders}
        folderId={note.folderId}
      />
    </div>
  );
});

const NotesHandler = ({ onOpenSettings }) => {
  const [notes, setNotes] = useState(loadNotes);
  const [folders, setFolders] = useState(loadFolders);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("updated-desc");
  const [noteTypeFilter, setNoteTypeFilter] = useState("all");
  const [notesViewMode, setNotesViewMode] = useState("cards");
  const [focusedNoteId, setFocusedNoteId] = useState(null);
  const [pendingDeleteNoteId, setPendingDeleteNoteId] = useState(null);
  const [pendingDeleteFolder, setPendingDeleteFolder] = useState(null);
  const [showOCRModal, setShowOCRModal] = useState(false);
  // sidebarOpen: the phone drawer. sidebarHidden: the desktop sidebar closed, like ModelLoop's
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(() => {
    try {
      return localStorage.getItem("sidebar:collapsed") === "true";
    } catch {
      return false;
    }
  });
  const setSidebarHiddenSaved = useCallback((hidden) => {
    setSidebarHidden(hidden);
    try {
      localStorage.setItem("sidebar:collapsed", String(hidden));
    } catch {}
  }, []);
  const openSidebar = useCallback(() => {
    if (window.matchMedia("(max-width: 760px)").matches) setSidebarOpen(true);
    else setSidebarHiddenSaved(false);
  }, [setSidebarHiddenSaved]);
  const [ocrImage, setOcrImage] = useState(null);
  const [ocrText, setOcrText] = useState("");
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState("");
  const [pinnedIds, setPinnedIds] = useState(loadPinnedIds);

  // Save everything to this browser whenever it changes
  useEffect(() => {
    saveNotes(notes);
  }, [notes]);

  useEffect(() => {
    saveFolders(folders);
  }, [folders]);

  useEffect(() => {
    savePinnedIds(pinnedIds);
  }, [pinnedIds]);

  // Pick up changes made in another tab
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === NOTES_KEY) setNotes(loadNotes());
      else if (e.key === FOLDERS_KEY) setFolders(loadFolders());
      else if (e.key === PINNED_KEY) setPinnedIds(loadPinnedIds());
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const handleCreateFolder = useCallback((name, parentId) => {
    setFolders((prev) => [
      ...prev,
      { id: newId(), name, parentId: parentId || null, createdAt: Date.now() },
    ]);
  }, []);

  const updateFolder = useCallback((folderId, patch) => {
    setFolders((prev) =>
      prev.map((f) => (f.id === folderId ? { ...f, ...patch } : f)),
    );
  }, []);

  const handleRenameFolder = useCallback(
    (folderId, name) => updateFolder(folderId, { name }),
    [updateFolder],
  );

  const handleDeleteFolder = useCallback((folderId, folderName) => {
    setPendingDeleteFolder({ id: folderId, name: folderName });
  }, []);

  const handleMoveFolder = useCallback(
    (folderId, parentId) => updateFolder(folderId, { parentId }),
    [updateFolder],
  );

  const handleColorFolder = useCallback(
    (folderId, color) => updateFolder(folderId, { color }),
    [updateFolder],
  );

  const handleMoveNote = useCallback((noteId, folderId) => {
    setNotes((prev) =>
      prev.map((n) => (n.id === noteId ? { ...n, folderId } : n)),
    );
  }, []);

  useEffect(() => {
    const cutoff = Date.now() - AUTO_DELETE_AFTER_MS;
    const hasExpired = notes.some(
      (note) => note.deletedAt && Number(note.deletedAt) <= cutoff,
    );
    if (!hasExpired) return;

    setNotes((prev) =>
      prev.filter(
        (note) => !(note.deletedAt && Number(note.deletedAt) <= cutoff),
      ),
    );
  }, [notes]);

  useEffect(() => {
    if (focusedNoteId == null) return;
    const stillExists = notes.some(
      (n) => String(n.id) === String(focusedNoteId),
    );
    if (!stillExists) setFocusedNoteId(null);
  }, [focusedNoteId, notes]);

  const handleNewNote = useCallback(
    (noteType = "text") => {
      const targetFolderId =
        selectedFolderId && selectedFolderId !== TRASH_VIEW_ID
          ? selectedFolderId
          : null;
      const now = Date.now();
      const newNote = {
        id: newId(),
        content: "",
        title: "",
        font: noteType === "code" ? "mono" : "nunito",
        fontSize: 16,
        theme: "default",
        noteType,
        language: "python",
        codeColorTheme: noteType === "code" ? "night-owl" : undefined,
        showLineNumbers: noteType === "code" ? false : undefined,
        folderId: targetFolderId,
        createdAt: now,
        lastModified: now,
      };
      setNotes((prev) => [newNote, ...prev]);
      setEditingNoteId(newNote.id);
    },
    [selectedFolderId],
  );

  const workerRef = useRef(null);

  const handleOCRImageSelect = useCallback(async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;

    // Validate file type and size (max 10MB)
    if (!file.type.startsWith("image/")) {
      setOcrError("Please select a valid image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setOcrError("Image file is too large. Maximum size is 10MB.");
      return;
    }

    setOcrError("");
    setOcrLoading(true);
    setOcrText("");

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const imageData = event.target?.result;
          setOcrImage(imageData);

          // Create Tesseract worker if not already created
          if (!workerRef.current) {
            workerRef.current = await createWorker();
          }

          // Extract text using Tesseract.js
          const result = await workerRef.current.recognize(imageData);

          setOcrText(result.data.text || "");
        } catch (error) {
          console.error("OCR Error:", error);
          setOcrError(
            "Failed to extract text from image. Please try another image.",
          );
        } finally {
          setOcrLoading(false);
        }
      };
      reader.onerror = () => {
        setOcrError("Failed to read file");
        setOcrLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("File read error:", error);
      setOcrError("Failed to read file");
      setOcrLoading(false);
    }
  }, []);

  const handleOCRCancel = useCallback(() => {
    setShowOCRModal(false);
    setOcrImage(null);
    setOcrText("");
    setOcrError("");
  }, []);

  const handleOCRCreateNote = useCallback(() => {
    const trimmedText = ocrText.trim();
    if (!trimmedText) {
      setOcrError("No text to extract");
      return;
    }
    const now = Date.now();
    setNotes((prev) => [
      {
        id: newId(),
        content: trimmedText,
        title: `OCR Scan ${new Date().toLocaleString()}`,
        font: "nunito",
        fontSize: 16,
        theme: "default",
        folderId: null,
        createdAt: now,
        lastModified: now,
      },
      ...prev,
    ]);
    handleOCRCancel();
  }, [ocrText, handleOCRCancel]);

  // Cleanup OCR worker on unmount
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate().catch(() => {
          // Worker cleanup error, ignore
        });
      }
    };
  }, []);

  const handleRemove = useCallback((id) => {
    setPendingDeleteNoteId(id);
  }, []);

  // First delete moves a note to Recently deleted; deleting it there removes it for good
  const confirmDeleteNote = useCallback(() => {
    const id = pendingDeleteNoteId == null ? null : String(pendingDeleteNoteId);
    setPendingDeleteNoteId(null);
    if (id == null) return;

    const note = notes.find((n) => String(n.id) === id);
    if (!note) return;

    setPinnedIds((prev) => prev.filter((p) => p !== id));
    if (String(focusedNoteId) === id) setFocusedNoteId(null);
    if (String(editingNoteId) === id) setEditingNoteId(null);

    if (note.deletedAt) {
      setNotes((prev) => prev.filter((n) => String(n.id) !== id));
    } else {
      const deletedAt = Date.now();
      setNotes((prev) =>
        prev.map((n) =>
          String(n.id) === id ? { ...n, deletedAt, lastModified: deletedAt } : n,
        ),
      );
    }
  }, [editingNoteId, focusedNoteId, notes, pendingDeleteNoteId]);

  const handleRestoreNote = useCallback((id) => {
    const restoredAt = Date.now();
    setNotes((prev) =>
      prev.map((n) =>
        String(n.id) === String(id)
          ? { ...n, deletedAt: null, lastModified: restoredAt }
          : n,
      ),
    );
  }, []);

  const handleEmptyRecentlyDeleted = useCallback(() => {
    const deletedIds = notes
      .filter((note) => note.deletedAt)
      .map((note) => String(note.id));

    if (deletedIds.length === 0) return;
    if (!window.confirm("Empty Recently Deleted? This cannot be undone.")) {
      return;
    }

    setNotes((prev) => prev.filter((note) => !note.deletedAt));
    setPinnedIds((prev) =>
      prev.filter((id) => !deletedIds.includes(String(id))),
    );
  }, [notes]);

  const handleTogglePin = useCallback((id) => {
    const stringId = String(id);
    setPinnedIds((prev) =>
      prev.includes(stringId)
        ? prev.filter((p) => p !== stringId)
        : [stringId, ...prev],
    );
  }, []);

  const updateNote = useCallback((id, patch, options = {}) => {
    const commit = options.commit === true;
    const committedAt = Date.now();
    setNotes((prev) =>
      prev.map((n) =>
        n.id === id
          ? {
              ...n,
              ...patch,
              ...(commit ? { lastModified: committedAt } : {}),
            }
          : n,
      ),
    );
  }, []);

  const commitNoteEdits = useCallback(
    async (id) => {
      await updateNote(id, {}, { commit: true });
    },
    [updateNote],
  );

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const activeNotes = useMemo(
    () => notes.filter((note) => !note.deletedAt),
    [notes],
  );

  const deletedNotes = useMemo(
    () => notes.filter((note) => Boolean(note.deletedAt)),
    [notes],
  );

  const confirmDeleteFolder = useCallback(() => {
    if (!pendingDeleteFolder) return;

    const { id: folderId } = pendingDeleteFolder;

    const collectIds = (id) => {
      const ids = [id];
      folders
        .filter((f) => f.parentId === id)
        .forEach((child) => ids.push(...collectIds(child.id)));
      return ids;
    };

    const idsToRemove = collectIds(folderId);
    setFolders((prev) => prev.filter((f) => !idsToRemove.includes(f.id)));
    setNotes((prev) =>
      prev.map((n) =>
        idsToRemove.includes(n.folderId) ? { ...n, folderId: null } : n,
      ),
    );
    if (selectedFolderId && idsToRemove.includes(selectedFolderId)) {
      setSelectedFolderId(null);
    }
    setPendingDeleteFolder(null);
  }, [folders, pendingDeleteFolder, selectedFolderId]);

  const cancelDeleteFolder = useCallback(() => {
    setPendingDeleteFolder(null);
  }, []);

  const folderChildrenByParent = useMemo(() => {
    const map = new Map();
    for (const folder of folders) {
      const parentKey =
        folder.parentId == null ? null : String(folder.parentId);
      const children = map.get(parentKey) || [];
      children.push(String(folder.id));
      map.set(parentKey, children);
    }
    return map;
  }, [folders]);

  const collectDescendantFolderIds = useCallback(
    (folderId) => {
      const rootId = String(folderId);
      const ids = new Set([rootId]);
      const queue = [rootId];

      while (queue.length > 0) {
        const current = queue.shift();
        const children = folderChildrenByParent.get(current) || [];
        for (const childId of children) {
          if (ids.has(childId)) continue;
          ids.add(childId);
          queue.push(childId);
        }
      }

      return ids;
    },
    [folderChildrenByParent],
  );

  const isTrashView = selectedFolderId === TRASH_VIEW_ID;

  const selectedFolderScopeIds = useMemo(() => {
    if (selectedFolderId == null || selectedFolderId === TRASH_VIEW_ID) {
      return null;
    }
    return collectDescendantFolderIds(selectedFolderId);
  }, [collectDescendantFolderIds, selectedFolderId]);

  const folderFilteredNotes = isTrashView
    ? deletedNotes
    : selectedFolderScopeIds
      ? activeNotes.filter((note) => {
          if (note.folderId == null) return false;
          return selectedFolderScopeIds.has(String(note.folderId));
        })
      : activeNotes;
  const typeFilteredNotes =
    noteTypeFilter === "all"
      ? folderFilteredNotes
      : folderFilteredNotes.filter((note) => {
          if (noteTypeFilter === "code") return note.noteType === "code";
          return (note.noteType || "text") !== "code";
        });

  const visibleNotes = normalizedQuery
    ? typeFilteredNotes.filter((note) => {
        const title = (note.title || "").toLowerCase();
        const content = (note.content || "").toLowerCase();
        return (
          title.includes(normalizedQuery) || content.includes(normalizedQuery)
        );
      })
    : typeFilteredNotes;

  const toTimestamp = (val) => {
    if (typeof val === "number") return val;
    if (!val) return 0;
    const ts = new Date(val).getTime();
    return Number.isNaN(ts) ? 0 : ts;
  };

  // Sort to show pinned notes at the top, then apply selected ordering
  const sortedNotes = [...visibleNotes].sort((a, b) => {
    if (isTrashView) {
      return toTimestamp(b.deletedAt) - toTimestamp(a.deletedAt);
    }

    const aIsPinned = pinnedIds.includes(String(a.id)) ? 1 : 0;
    const bIsPinned = pinnedIds.includes(String(b.id)) ? 1 : 0;
    if (bIsPinned !== aIsPinned) return bIsPinned - aIsPinned;

    if (sortBy === "updated-desc") {
      return toTimestamp(b.lastModified) - toTimestamp(a.lastModified);
    }
    if (sortBy === "updated-asc") {
      return toTimestamp(a.lastModified) - toTimestamp(b.lastModified);
    }
    if (sortBy === "created-desc") {
      return toTimestamp(b.createdAt) - toTimestamp(a.createdAt);
    }
    if (sortBy === "created-asc") {
      return toTimestamp(a.createdAt) - toTimestamp(b.createdAt);
    }
    if (sortBy === "title-desc") {
      return (b.title || "").localeCompare(a.title || "", undefined, {
        sensitivity: "base",
      });
    }

    return (a.title || "").localeCompare(b.title || "", undefined, {
      sensitivity: "base",
    });
  });

  const displayedNotes = focusedNoteId
    ? sortedNotes.filter((n) => String(n.id) === String(focusedNoteId))
    : sortedNotes;

  const folderById = useMemo(
    () => new Map(folders.map((f) => [String(f.id), f])),
    [folders],
  );

  const selectedFolderPath = useMemo(() => {
    if (selectedFolderId == null || selectedFolderId === TRASH_VIEW_ID)
      return [];

    const path = [];
    const seen = new Set();
    let cursor = folderById.get(String(selectedFolderId));

    while (cursor && !seen.has(String(cursor.id))) {
      path.unshift(cursor);
      seen.add(String(cursor.id));
      cursor =
        cursor.parentId == null
          ? null
          : folderById.get(String(cursor.parentId));
    }

    return path;
  }, [folderById, selectedFolderId]);

  const selectedFolderChildren = useMemo(() => {
    if (selectedFolderId == null || selectedFolderId === TRASH_VIEW_ID)
      return [];
    return folders.filter(
      (folder) => String(folder.parentId) === String(selectedFolderId),
    );
  }, [folders, selectedFolderId]);

  const noteCountByFolder = useMemo(() => {
    const directCounts = new Map();
    for (const note of activeNotes) {
      if (note.folderId == null) continue;
      const key = String(note.folderId);
      directCounts.set(key, (directCounts.get(key) || 0) + 1);
    }

    const totals = new Map();
    const computeTotal = (folderId) => {
      const key = String(folderId);
      if (totals.has(key)) return totals.get(key);

      let total = directCounts.get(key) || 0;
      const children = folderChildrenByParent.get(key) || [];
      for (const childId of children) {
        total += computeTotal(childId);
      }

      totals.set(key, total);
      return total;
    };

    for (const folder of folders) {
      computeTotal(folder.id);
    }

    return totals;
  }, [activeNotes, folderChildrenByParent, folders]);

  const totalNotesCount = activeNotes.length;
  const totalDeletedCount = deletedNotes.length;

  const hasSearch = normalizedQuery.length > 0;
  const focusedNote = focusedNoteId
    ? notes.find((n) => String(n.id) === String(focusedNoteId)) || null
    : null;

  const pendingDeleteNote = pendingDeleteNoteId
    ? notes.find((n) => String(n.id) === String(pendingDeleteNoteId)) || null
    : null;

  const currentFolder = selectedFolderPath[selectedFolderPath.length - 1];
  const pageTitle = isTrashView
    ? "Recently deleted"
    : currentFolder?.name || "All notes";
  // The top bar says what you're doing right now
  const editingNote = editingNoteId
    ? notes.find((n) => String(n.id) === String(editingNoteId))
    : null;
  const placeName = isTrashView
    ? "Recently deleted"
    : currentFolder?.name || "all notes";
  const activityTitle = editingNote
    ? `Editing ${editingNote.title?.trim() || (editingNote.noteType === "code" ? "a code note" : "a new note")}`
    : focusedNote
      ? `Reading ${focusedNote.title?.trim() || "an untitled note"}`
      : hasSearch
        ? `Searching ${placeName}`
        : isTrashView
          ? "Reviewing Recently deleted"
          : `Browsing ${placeName}`;
  const noteCountLabel = `${displayedNotes.length} ${
    displayedNotes.length === 1 ? "note" : "notes"
  }`;
  const pageSubtitle = isTrashView
    ? "Notes here are deleted for good after 10 days."
    : selectedFolderPath.length > 1
      ? `${selectedFolderPath
          .slice(0, -1)
          .map((f) => f.name)
          .join(" / ")} · ${noteCountLabel}`
      : noteCountLabel;

  // One filled accent icon and one short line
  const emptyState = (title, icon = "note") => (
    <div className="empty-state notes-empty">
      <svg
        className="empty-state-icon"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        {icon === "trash" ? (
          <path d="M9 3a1 1 0 0 0-1 1v1H4a1 1 0 0 0 0 2h1l1 13a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-13h1a1 1 0 1 0 0-2h-4V4a1 1 0 0 0-1-1H9z" />
        ) : icon === "search" ? (
          <path d="M10.5 3a7.5 7.5 0 0 1 5.96 12.05l4.25 4.24a1 1 0 0 1-1.42 1.42l-4.24-4.25A7.5 7.5 0 1 1 10.5 3z" />
        ) : icon === "filter" ? (
          <path d="M3.5 4h17a1 1 0 0 1 .78 1.63L15 13.4V19a1 1 0 0 1-.55.9l-4 2A1 1 0 0 1 9 21v-7.6L2.72 5.63A1 1 0 0 1 3.5 4z" />
        ) : icon === "folder" ? (
          <path d="M3 5a2 2 0 0 1 2-2h4.2a2 2 0 0 1 1.4.6L12.4 5H19a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5z" />
        ) : (
          <path d="M6 2h8l6 6v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm7 1.5V9h5.5L13 3.5z" />
        )}
      </svg>
      <p className="empty-state-title">{title}</p>
    </div>
  );

  // Creating a note answers with the "send" chime
  const createNote = (noteType) => {
    sounds.play("send");
    handleNewNote(noteType);
  };

  return (
    <>
      <div className="app-shell">
        <FolderSidebar
          folders={folders}
          selectedFolderId={selectedFolderId}
          onSelectFolder={(id) => {
            setSelectedFolderId(id);
            setFocusedNoteId(null);
            setSidebarOpen(false);
          }}
          onCreateFolder={handleCreateFolder}
          onRenameFolder={handleRenameFolder}
          onDeleteFolder={handleDeleteFolder}
          onMoveFolder={handleMoveFolder}
          onColorFolder={handleColorFolder}
          hidden={sidebarHidden}
          onHide={() => setSidebarHiddenSaved(true)}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          noteCountByFolder={noteCountByFolder}
          totalNotesCount={totalNotesCount}
          recentlyDeletedCount={totalDeletedCount}
          trashViewId={TRASH_VIEW_ID}
        />
        <div className="notes-main">
          <TopBar
            title={activityTitle}
            onOpenSidebar={openSidebar}
            sidebarHidden={sidebarHidden}
            onOpenSettings={onOpenSettings}
            actions={
              !isTrashView && (
                <button
                  type="button"
                  className="icon-btn stretchy"
                  onClick={() => setShowOCRModal(true)}
                  aria-label="Scan an image to text"
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  <span className="tooltip tooltip--left">Scan an image to text</span>
                </button>
              )
            }
          />

          <main className="notes-content">
          <PageHeader
            title={pageTitle}
            subtitle={pageSubtitle}
            actions={
              isTrashView ? (
                <button
                  type="button"
                  className="btn btn-danger confirm-destructive"
                  onClick={handleEmptyRecentlyDeleted}
                  disabled={displayedNotes.length === 0}
                >
                  Empty recently deleted
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn plays-own-sound"
                    onClick={() => createNote("code")}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="16 18 22 12 16 6" />
                      <polyline points="8 6 2 12 8 18" />
                    </svg>
                    Code note
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary plays-own-sound"
                    onClick={() => createNote("text")}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    New note
                  </button>
                </>
              )
            }
          />

          <div className="notes-toolbar">
            <label className="notes-search-wrap">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="20" y1="20" x2="16.5" y2="16.5" />
              </svg>
              <input
                className="field notes-search"
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search notes"
                aria-label="Search notes"
              />
            </label>
            <div className="notes-toolbar-right">
              <Dropdown
                options={NOTE_TYPE_OPTIONS}
                value={noteTypeFilter}
                onChange={setNoteTypeFilter}
                placeholder="Type"
              />
              <Dropdown
                options={SORT_OPTIONS}
                value={sortBy}
                onChange={setSortBy}
                placeholder="Sort"
              />
              {!isTrashView && (
                <button
                  type="button"
                  className={`icon-btn${notesViewMode === "list" ? " is-active" : ""}`}
                  onClick={() => {
                    if (notesViewMode === "list") {
                      setNotesViewMode("cards");
                      return;
                    }
                    setFocusedNoteId(null);
                    setNotesViewMode("list");
                  }}
                  aria-pressed={notesViewMode === "list"}
                  aria-label="Show titles only"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="8" y1="6" x2="21" y2="6" />
                    <line x1="8" y1="12" x2="21" y2="12" />
                    <line x1="8" y1="18" x2="21" y2="18" />
                    <circle cx="4" cy="6" r="1" />
                    <circle cx="4" cy="12" r="1" />
                    <circle cx="4" cy="18" r="1" />
                  </svg>
                  <span className="tooltip">
                    {notesViewMode === "list" ? "Show full notes" : "Show titles only"}
                  </span>
                </button>
              )}
            </div>
          </div>

          {selectedFolderChildren.length > 0 && (
            <nav className="folder-subfolder-links" aria-label="Subfolders">
              {selectedFolderChildren.map((folder) => (
                <button
                  key={folder.id}
                  type="button"
                  className="chip"
                  onClick={() => {
                    setSelectedFolderId(folder.id);
                    setFocusedNoteId(null);
                  }}
                >
                  {folder.name}
                  <span className="folder-subfolder-link-count">
                    {noteCountByFolder.get(String(folder.id)) || 0}
                  </span>
                </button>
              ))}
            </nav>
          )}

          {focusedNote && (
            <div className="note-focus-filter">
              <span>
                Showing <strong>{focusedNote.title || "Untitled note"}</strong>
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setFocusedNoteId(null)}
              >
                Show all
              </button>
            </div>
          )}

          {isTrashView ? (
            displayedNotes.length === 0 ? (
              emptyState("Recently deleted is empty", "trash")
            ) : (
              <div className="notes-title-list" role="list">
                {displayedNotes.map((n) => (
                  <div key={n.id} className="notes-row trash-note-item" role="listitem">
                    <div className="notes-row-text">
                      <span className="notes-row-title">
                        {n.title || "Untitled note"}
                      </span>
                      <span className="notes-row-meta">
                        Deleted {new Date(n.deletedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="notes-row-actions">
                      <button
                        type="button"
                        className="btn"
                        onClick={() => handleRestoreNote(n.id)}
                      >
                        Restore
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-danger"
                        onClick={() => handleRemove(n.id)}
                      >
                        Delete forever
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : displayedNotes.length === 0 ? (
            hasSearch
              ? emptyState("No notes match your search", "search")
              : noteTypeFilter !== "all"
                ? emptyState(
                    noteTypeFilter === "code"
                      ? "No code notes here"
                      : "No text notes here",
                    "filter",
                  )
                : currentFolder
                  ? emptyState("This folder is empty", "folder")
                  : emptyState("No notes yet")
          ) : notesViewMode === "list" ? (
            <div className="notes-title-list" role="list">
              {displayedNotes.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className="notes-row notes-title-list-item"
                  onClick={() => {
                    setFocusedNoteId(n.id);
                    setNotesViewMode("cards");
                  }}
                >
                  <span className="notes-row-title">
                    {n.title || "Untitled note"}
                  </span>
                  <span className="notes-row-badges">
                    {pinnedIds.includes(String(n.id)) && (
                      <span className="badge badge--accent">Pinned</span>
                    )}
                    <span className="badge">
                      {n.noteType === "code" ? "Code" : "Note"}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="notes-list">
              {displayedNotes.map((n) => (
                <NoteItem
                  key={n.id}
                  note={n}
                  updateNote={updateNote}
                  commitNoteEdits={commitNoteEdits}
                  handleRemove={handleRemove}
                  isPinned={pinnedIds.includes(String(n.id))}
                  onTogglePin={handleTogglePin}
                  folders={folders}
                  onMoveNote={handleMoveNote}
                  isEditing={editingNoteId === n.id}
                  setEditingNoteId={setEditingNoteId}
                />
              ))}
            </div>
          )}
          </main>
        </div>
      </div>

      {showOCRModal && (
        <div className="overlay" onClick={() => setShowOCRModal(false)}>
          <div
            className="modal ocr-modal-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ocr-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2 className="modal-title" id="ocr-title">
                  Scan an image to text
                </h2>
                <p className="modal-subtitle">
                  The text is read on your device and saved as a new note.
                </p>
              </div>
              <button
                type="button"
                className="icon-btn icon-btn--sm icon-btn--flat"
                onClick={handleOCRCancel}
                aria-label="Close"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="18" y1="6" x2="6" y2="18" />
                </svg>
              </button>
            </div>

            <div className="ocr-modal-content">
              {!ocrText ? (
                <div className="ocr-upload-section">
                  <label className="ocr-upload-label">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleOCRImageSelect}
                      disabled={ocrLoading}
                      style={{ display: "none" }}
                    />
                    <div className="ocr-upload-box">
                      {ocrLoading ? (
                        <div className="ocr-loading">
                          <span className="waiting-leaves" aria-hidden="true">
                            <i />
                            <i />
                            <i />
                          </span>
                          <p>Reading the image...</p>
                        </div>
                      ) : (
                        <>
                          <svg
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                            <circle cx="12" cy="13" r="4" />
                          </svg>
                          <p>Choose an image</p>
                          <p className="ocr-upload-hint">PNG or JPG with clear, printed text works best</p>
                        </>
                      )}
                    </div>
                  </label>
                </div>
              ) : (
                <div className="ocr-preview-section">
                  {ocrImage && (
                    <div className="ocr-image-preview">
                      <img src={ocrImage} alt="OCR Source" />
                    </div>
                  )}
                  <div className="ocr-text-preview">
                    <label htmlFor="ocr-text-area" className="section-label">
                      Extracted text
                    </label>
                    <textarea
                      id="ocr-text-area"
                      className="field ocr-text-area"
                      value={ocrText}
                      onChange={(e) => setOcrText(e.target.value)}
                      placeholder="Extracted text will appear here"
                    />
                  </div>
                </div>
              )}

              {ocrError && <div className="ocr-error">{ocrError}</div>}
            </div>

            <div className="modal-actions">
              <Button onClick={handleOCRCancel} className="btn-ghost">
                Cancel
              </Button>
              {ocrText ? (
                <Button
                  onClick={handleOCRCreateNote}
                  className="primary"
                  disabled={ocrLoading}
                >
                  Create note
                </Button>
              ) : (
                <Button
                  onClick={() =>
                    document.querySelector('.ocr-upload-label input[type="file"]')?.click()
                  }
                  className="primary"
                  disabled={ocrLoading}
                >
                  Choose image
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {pendingDeleteNote && (
        <div className="overlay" onClick={() => setPendingDeleteNoteId(null)}>
          <div
            className="modal confirm-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-note-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="modal-title" id="delete-note-title">
              {pendingDeleteNote.deletedAt
                ? "Delete this note for good?"
                : "Move this note to Recently deleted?"}
            </h2>
            <p className="modal-subtitle">
              {pendingDeleteNote.deletedAt
                ? "It will be removed permanently and cannot be recovered."
                : "You can restore it from Recently deleted for the next 10 days."}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setPendingDeleteNoteId(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn btn-primary${pendingDeleteNote.deletedAt ? " confirm-destructive" : ""}`}
                onClick={confirmDeleteNote}
              >
                {pendingDeleteNote.deletedAt ? "Delete forever" : "Move"}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteFolder && (
        <div className="overlay" onClick={cancelDeleteFolder}>
          <div
            className="modal confirm-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-folder-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="modal-title" id="delete-folder-title">
              Delete the folder "{pendingDeleteFolder.name}"?
            </h2>
            <p className="modal-subtitle">
              Notes inside it and its subfolders will move back to All notes.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={cancelDeleteFolder}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary confirm-destructive"
                onClick={confirmDeleteFolder}
              >
                Delete folder
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotesHandler;
